import React, { useState, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import { useTechnologyEmbedded } from '../hooks/useTechnologyEmbedded';
import PortalBackground from '../components/PortalBackground';
import { PYRAMID_LAYERS, TECH_TIERS, fetchTechTierMeta } from '../utils/techStackTiers';

const SoftwareOverview = ({ embedded: embeddedProp }) => {
  const { darkMode } = useTheme();
  const embedded = useTechnologyEmbedded(embeddedProp);
  const { tier, typeId, softwareId } = useParams();
  const navigate = useNavigate();
  const [software, setSoftware] = useState(null);
  const [typeName, setTypeName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [tierMeta, setTierMeta] = useState(() => TECH_TIERS.map((t) => ({ ...t, title: t.defaultTitle })));
  const tierIndex = Math.max(0, tierMeta.findIndex((t) => t.docId === tier));

  useEffect(() => {
    const fetchSoftwareData = async () => {
      try {
        console.log('URL params - tier:', tier, 'typeId:', typeId, 'softwareId:', softwareId);
        
        setTierMeta(await fetchTechTierMeta());

        // Fetch software data
        const softwareRef = doc(db, 'technologyStack', tier, 'types', typeId, 'softwares', softwareId);
        const softwareDoc = await getDoc(softwareRef);
        
        // Fetch type data
        const typeRef = doc(db, 'technologyStack', tier, 'types', typeId);
        const typeDoc = await getDoc(typeRef);
        
        if (softwareDoc.exists()) {
          const data = softwareDoc.data();
          setSoftware({
            id: softwareDoc.id,
            ...data,
            tier
          });
        } else {
          setError('Software not found');
        }
        
        if (typeDoc.exists()) {
          const typeData = typeDoc.data();
          console.log('Type data:', typeData);
          setTypeName(typeData.name || typeId.replace(/([A-Z])/g, ' $1').trim());
        } else {
          console.log('Type document not found for typeId:', typeId);
          setTypeName(typeId.replace(/([A-Z])/g, ' $1').trim());
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching software:', error);
        setError('Error loading software details');
        setIsLoading(false);
      }
    };

    fetchSoftwareData();
  }, [tier, typeId, softwareId]);

  if (isLoading) {
    return (
      <div className={embedded ? 'p-0' : 'min-h-screen p-6 relative'}>
        {!embedded && <PortalBackground />}
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center py-12">
            <BBALoader size={32} className="inline-block" />
            <p className={`mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading software details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={embedded ? 'p-0' : 'min-h-screen p-6 relative'}>
        {!embedded && <PortalBackground />}
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center py-12">
            <p className="text-red-600 text-lg">{error}</p>
            <button
              onClick={() => navigate('/portal/technology/all-software')}
              className="mt-4 text-red-600 hover:text-red-700"
            >
              Return to Software List
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'min-h-screen p-6 relative'}>
      {!embedded && <PortalBackground />}
      <div className="relative max-w-7xl mx-auto">
        {!embedded && (
          <div className="flex justify-between items-center mb-8">
            <BackButton to="/portal/technology/all-software" />
            <h1 className={`text-3xl font-bold tracking-tight text-gray-900 dark:text-white flex-1 text-center`}>Software Details</h1>
            <div className="w-[60px]" />
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-8">
            {/* Hero Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden`}
            >
              <div className="p-8">
                <div className="flex items-start gap-6">
                  {/* Logo */}
                  <div className="w-24 h-24 flex-shrink-0">
                    {software.image && !imageError ? (
                      <img 
                        src={software.image} 
                        alt={software.name}
                        className="w-full h-full object-contain"
                        onError={() => setImageError(true)}
                      />
                    ) : (
                      <div className={`w-full h-full ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-lg flex items-center justify-center`}>
                        <span className={`${darkMode ? 'text-gray-500' : 'text-gray-400'} text-4xl`}>?</span>
                      </div>
                    )}
                  </div>

                  {/* Title and Badges */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {software.name}
                        {software.suggested && (
                          <span className="ml-2 text-red-600">★</span>
                        )}
                      </h2>
                      {software.open && (
                        <span className="bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full">
                          Open API
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
                        {typeName}
                      </span>
                      <span className={`text-sm font-medium px-3 py-1 rounded-full text-white ${PYRAMID_LAYERS[tierIndex].color}`}>
                        {tierMeta[tierIndex]?.docId === software.tier ? tierMeta[tierIndex].title : software.tier}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Description */}
            {software.description && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden`}
              >
                <div className="p-8">
                  <h3 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4`}>Description</h3>
                  <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} leading-relaxed`}>{software.description}</p>
                </div>
              </motion.div>
            )}

            {/* Use Cases */}
            {software.useCases && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden`}
              >
                <div className="p-8">
                  <h3 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4`}>Features & Use Cases</h3>
                  <ul className="space-y-2">
                    {Array.isArray(software.useCases) ? (
                      software.useCases.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-red-500 mr-2">•</span>
                          <span className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{feature}</span>
                        </li>
                      ))
                    ) : (
                      software.useCases.split(',').map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-red-500 mr-2">•</span>
                          <span className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{feature.trim()}</span>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              </motion.div>
            )}
          </div>

          {/* Right Column - Pricing and Signup */}
          <div className="space-y-8">
            {/* Pricing Plans */}
            {software.pricing && software.pricing.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden`}
              >
                <div className="p-8">
                  <h3 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4`}>Pricing Plans</h3>
                  <div className="space-y-4">
                    {software.pricing.map((plan, index) => (
                      <div key={index} className={`border border-gray-200 dark:border-gray-700 rounded-xl p-4`}>
                        <div className="flex justify-between items-center mb-2">
                          <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{plan.name}</span>
                          <div className={`text-right ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {plan.tieredPricing ? (
                              <div>
                                <div className="text-sm">Tiered Pricing</div>
                                <div className="text-xs">See tiers below</div>
                              </div>
                            ) : plan.variablePricing ? (
                              <div>
                                <div className="text-sm">Variable Pricing</div>
                                <div className="text-xs">Contact for pricing</div>
                              </div>
                            ) : (
                              <div>
                                {plan.monthlyCost && (
                                  <div>${Number(plan.monthlyCost).toLocaleString()}/month</div>
                                )}
                                {plan.annualCost && (
                                  <div className="text-sm">${Number(plan.annualCost).toLocaleString()}/year</div>
                                )}
                                {plan.oneTimeCost && (
                                  <div className="text-sm">${Number(plan.oneTimeCost).toLocaleString()} one-time</div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        {plan.description && (
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-3`}>{plan.description}</p>
                        )}
                        
                        {/* Tiered Pricing Display */}
                        {plan.tieredPricing && plan.tiers && plan.tiers.length > 0 && (
                          <div className="mt-3">
                            <div className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Pricing Tiers:</div>
                            <div className="space-y-2">
                              {plan.tiers.map((tier, tierIndex) => (
                                <div key={tierIndex} className={`text-xs ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-600'} p-2 rounded`}>
                                  <div className="flex justify-between">
                                    <span>
                                      {tier.minUsers && tier.maxUsers 
                                        ? `${tier.minUsers}-${tier.maxUsers} users`
                                        : tier.minUsers 
                                        ? `${tier.minUsers}+ users`
                                        : 'Custom range'
                                      }
                                    </span>
                                    <span>
                                      {tier.baseMonthlyCost && `${tier.baseMonthlyCost}/month`}
                                      {tier.pricePerUser && ` + ${tier.pricePerUser}/user`}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Signup Button */}
            {(software.signupUrl || software.website) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden`}
              >
                <div className="p-8">
                  <a
                    href={software.signupUrl || software.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-red-700 transition-colors"
                  >
                    Sign Up Now
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SoftwareOverview; 